import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient } from 'react-apollo';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import debounce from 'debounce-promise';

import Spinner from '../global/Spinner';
import Button from '../global/Button';
import MultiSelect from '../global/MultiSelect';
import useAlbumFolderForFaceRecognitionDisabled from '../global/hooks/useAlbumFolderForFaceRecognitionDisabled';
import { AlbumFolderForFaceRecognitionDisabled } from '../../graphql/queries/album-folder';
import { addSelectedRosterIdentity } from '../../actions/discover-album-folder';
import { ALBUM_FOLDER_ONBOARDING_TYPE } from '../../constants/album-folder';
import { PRICING_CATEGORY_TYPE } from '../../constants/pricing-categories';
import { HIERARCHY_GRADES_ENUM_LABELS } from '../../constants/hierarchy-grades';
import { shouldDisplayBrandAttribution } from '../../util/discover-album-folder';
import PhotographerAttributionBanner from './PhotographerAttributionBanner';
import WaldoSpeechBubble from '../global/WaldoSpeechBubble';
import OnboardingLayoutFooter from '../../apps/temp/views/onboarding/OnboardingLayout/components/OnboardingLayoutFooter';
import OnboardingLayoutScrollableContent from '../../apps/temp/views/onboarding/OnboardingLayout/components/OnboardingLayoutScrollableContent';

const propTypes = {
  albumFolderId: PropTypes.string,
};

const DiscoverAlbumFolderNoFrJoin = ({ albumFolderId }) => {
  const { albumFolder, loading } = useAlbumFolderForFaceRecognitionDisabled({
    albumFolderId,
  });

  const apolloClient = useApolloClient();
  const dispatch = useDispatch();
  const history = useHistory();
  const selectedRosterIdentities = useSelector(
    state => state.discoverAlbumFolder.selectedRosterIdentities,
  );

  const [grade, setGrade] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);

  if (loading || !albumFolder) {
    return (
      <div className="text-center mt-5">
        <Spinner />
      </div>
    );
  }

  const grades = albumFolder.grades?.records ?? [];
  const isRefIdJoin =
    albumFolder.onboarding_type === ALBUM_FOLDER_ONBOARDING_TYPE.REF_ID;
  const isGradeJoin =
    albumFolder.onboarding_type === ALBUM_FOLDER_ONBOARDING_TYPE.GRADE;
  const isFullNameJoin =
    albumFolder.onboarding_type === ALBUM_FOLDER_ONBOARDING_TYPE.FULL_NAME;
  const hasGrades = grades.length > 0;
  // FULL_NAME searches by name alone. GRADE narrows by grade first, but falls
  // back to a name-only search when the event has no grades to pick from.
  const isNameOnlyJoin = isFullNameJoin || (isGradeJoin && !hasGrades);
  const isSchoolAndAcademics = (
    albumFolder.pricing_categories?.records ?? []
  ).some(
    r =>
      r?.pricing_category?.type === PRICING_CATEGORY_TYPE.SCHOOL_AND_ACADEMICS,
  );
  const subjectTerm = isSchoolAndAcademics ? 'Student' : 'Member';

  const rosterIdentityToOption = rosterIdentity => {
    const albumFolderLink = (rosterIdentity?.links?.records ?? []).find(
      r => r.album_folder?.id === albumFolderId,
    );
    const refIdSuffix = albumFolderLink?.ref_id
      ? `#***${albumFolderLink.ref_id.slice(-3)}`
      : '';

    return {
      label: `${rosterIdentity.first_name} ${rosterIdentity.last_name}`,
      sublabel: refIdSuffix,
      value: rosterIdentity.id,
      rosterIdentity,
    };
  };

  const searchRosterIdentities = async (query = '') => {
    const trimmed = query.trim();
    if (!trimmed) return [];

    const variables = {
      id: albumFolderId,
      include_roster_identity_data: true,
    };

    if (isRefIdJoin) {
      variables.exact_ref_id = trimmed;
    } else {
      const [exactFirstName, ...exactLastName] = trimmed.split(' ');
      if (!exactFirstName || !exactLastName?.length) {
        return [];
      }
      if (!isNameOnlyJoin) {
        variables.grade = grade;
      }
      variables.exact_first_name = exactFirstName;
      variables.exact_last_name = exactLastName.join(' ');
    }

    const { data: { albumFolder: albumFolderWithFoundRis } = {} } =
      await apolloClient.query({
        query: AlbumFolderForFaceRecognitionDisabled,
        variables,
        fetchPolicy: 'network-only',
      });

    const records = albumFolderWithFoundRis.roster_identities?.records ?? [];
    return records.map(rosterIdentityToOption);
  };

  const searchRosterIdentitiesDebounced = debounce(searchRosterIdentities, 500);

  const onContinue = () => {
    if (!selectedOption) return;
    const { rosterIdentity } = selectedOption;
    dispatch(addSelectedRosterIdentity(rosterIdentity));
    history.push(`/discover/album-folder/${albumFolderId}/no-fr-join/confirm`);
  };

  const canSearch = isRefIdJoin || isNameOnlyJoin || !!grade;
  const rosterIdentity = selectedOption?.rosterIdentity;

  return (
    <Fragment>
      {shouldDisplayBrandAttribution(albumFolder) && (
        <PhotographerAttributionBanner account={albumFolder.account} />
      )}
      <WaldoSpeechBubble position="right" className="mb-4">
        Tell me who you want me to look for!
      </WaldoSpeechBubble>

      <OnboardingLayoutScrollableContent className="w-100">
        {isRefIdJoin ? (
          <div className="w-100 mb-4">
            <label htmlFor="no-fr-student-id" className="text-uppercase small">
              What is their {subjectTerm} ID?
            </label>
            <MultiSelect
              id="no-fr-student-id"
              placeholder={`Enter ${subjectTerm} ID...`}
              value={selectedOption}
              defaultOptions={[]}
              loadOptions={searchRosterIdentitiesDebounced}
              getOptionValue={item => item.value}
              formatOptionLabel={(option, context) => {
                if (!context?.inputValue) {
                  return (
                    <div className="w-100 d-flex justify-content-between align-items-center">
                      {option.label}
                      {option.sublabel && (
                        <span className="text-sm text-muted ml-2">
                          {option.sublabel}
                        </span>
                      )}
                    </div>
                  );
                }

                return (
                  <div className="d-flex flex-column">
                    {option.label}
                    {option.sublabel && (
                      <span className="text-sm text-muted">
                        {option.sublabel}
                      </span>
                    )}
                  </div>
                );
              }}
              noOptionsMessage={({ inputValue }) =>
                inputValue
                  ? 'No matches found'
                  : `Search by ${subjectTerm} ID...`
              }
              isClearable
              onChange={option => setSelectedOption(option)}
            />
          </div>
        ) : (
          <Fragment>
            {!isNameOnlyJoin && (
              <div className="w-100 mb-4">
                <label htmlFor="no-fr-grade" className="text-uppercase small">
                  What grade are they in?
                </label>
                <select
                  id="no-fr-grade"
                  className="form-control"
                  value={grade}
                  onChange={e => {
                    setGrade(e.target.value);
                    setSelectedOption(null);
                  }}
                >
                  <option value="">Select Grade</option>
                  {grades.map(({ id, grade: gradeValue }) => (
                    <option key={id} value={gradeValue}>
                      {HIERARCHY_GRADES_ENUM_LABELS[gradeValue] ?? gradeValue}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {canSearch && (
              <div className="w-100 mb-4">
                <label htmlFor="no-fr-name" className="text-uppercase small">
                  What is their name?
                </label>
                <MultiSelect
                  id="no-fr-name"
                  placeholder="Search name here..."
                  value={selectedOption}
                  defaultOptions={[]}
                  loadOptions={searchRosterIdentitiesDebounced}
                  getOptionValue={item => item.value}
                  formatOptionLabel={(option, context) => {
                    if (!context?.inputValue) {
                      return (
                        <div className="w-100 d-flex justify-content-between align-items-center">
                          {option.label}
                          {option.sublabel && (
                            <span className="text-sm text-muted ml-2">
                              {option.sublabel}
                            </span>
                          )}
                        </div>
                      );
                    }

                    return (
                      <div className="d-flex flex-column">
                        {option.label}
                        {option.sublabel && (
                          <span className="text-sm text-muted">
                            {option.sublabel}
                          </span>
                        )}
                      </div>
                    );
                  }}
                  noOptionsMessage={({ inputValue }) =>
                    inputValue ? 'No matches found' : 'Search by full name...'
                  }
                  isClearable
                  onChange={option => setSelectedOption(option)}
                />
              </div>
            )}
          </Fragment>
        )}
      </OnboardingLayoutScrollableContent>
      {(rosterIdentity || selectedRosterIdentities?.length > 0) && (
        <OnboardingLayoutFooter>
          {rosterIdentity && (
            <Button btnSize="lg" className="mt-3" onClick={onContinue}>
              View {rosterIdentity.first_name}&apos;s photos
            </Button>
          )}
          {selectedRosterIdentities.length > 0 && (
            <Button
              btnStyle="link"
              className="mt-3"
              onClick={() => {
                history.push(
                  `/discover/album-folder/${albumFolderId}/no-fr-join/confirm`,
                );
              }}
            >
              Never mind, back to confirmation
            </Button>
          )}
        </OnboardingLayoutFooter>
      )}
    </Fragment>
  );
};

DiscoverAlbumFolderNoFrJoin.propTypes = propTypes;

export default DiscoverAlbumFolderNoFrJoin;
